import {
  useGetChatSessionsQuery,
  usePatchChatSessionMutation,
} from "@api/chat";
import { IconWithTooltip } from "@components/IconWithTooltip";
import LoadingSpinner from "@components/LoadingSpinner";
import { Bars3Icon, PlusIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { useToastEffect } from "@hooks/hooks";
import { getDateOffset } from "@utils/date";
import { useRequiredParams } from "@utils/params";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { ChatSessionCard } from "./ChatSessionCard";

export function ChatSessionDrawer({
  children,
}: React.HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation("chatPage");

  const drawerId = useId();

  const { courseId } = useRequiredParams(["courseId"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");

  const { data: chatSessions } = useGetChatSessionsQuery(courseId);

  const currentChatSession = useMemo(() => {
    if (!chatSessions || chatSessions.length === 0) {
      return null;
    }
    return chatSessions.find((session) => session.id === sessionId);
  }, [chatSessions, sessionId]);

  const [
    patchChatSession,
    { error: patchChatSessionError, isError: patchChatSessionIsError },
  ] = usePatchChatSessionMutation();

  useToastEffect({
    isError: patchChatSessionIsError,
    error: patchChatSessionError,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    if (sessionId) {
      setDrawerOpen(false);
    }
  }, [sessionId]);

  const {
    todaySessions,
    yesterdaySessions,
    last7DaysSessions,
    last30DaysSessions,
  } = useMemo(() => {
    if (!chatSessions) {
      return {
        todaySessions: [],
        yesterdaySessions: [],
        last7DaysSessions: [],
        last30DaysSessions: [],
      };
    }

    const yesterday = getDateOffset(1);
    const dayBeforeYesterday = getDateOffset(2);
    const sevenDaysAgo = getDateOffset(7);
    const thirtyDaysAgo = getDateOffset(30);

    return {
      todaySessions: chatSessions.filter(
        (session) =>
          new Date(session.created).toDateString() ===
          new Date().toDateString(),
      ),
      yesterdaySessions: chatSessions.filter(
        (session) =>
          new Date(session.created).toDateString() === yesterday.toDateString(),
      ),
      last7DaysSessions: chatSessions.filter(
        (session) =>
          new Date(session.created) > sevenDaysAgo &&
          new Date(session.created) <= dayBeforeYesterday,
      ),
      last30DaysSessions: chatSessions.filter(
        (session) =>
          new Date(session.created) > thirtyDaysAgo &&
          new Date(session.created) <= sevenDaysAgo,
      ),
    };
  }, [chatSessions]);

  const sessionGroups = [
    { sessions: todaySessions, title: t("chatSession.todayHeader") },
    { sessions: yesterdaySessions, title: t("chatSession.yesterdayHeader") },
    { sessions: last7DaysSessions, title: t("chatSession.last7DaysHeader") },
    { sessions: last30DaysSessions, title: t("chatSession.last30DaysHeader") },
  ];

  return (
    <div className="drawer flex h-full flex-col">
      <input
        id={drawerId}
        type="checkbox"
        className="drawer-toggle"
        checked={drawerOpen}
        onChange={(e) => setDrawerOpen(e.target.checked)}
      />
      <div className="drawer-side">
        <label
          htmlFor={drawerId}
          className="drawer-overlay"
          aria-label={t("chatSession.closeOverlay")}
        />
        {!chatSessions ? (
          <LoadingSpinner />
        ) : chatSessions.length === 0 ? (
          t("chatSession.noChats")
        ) : (
          <div className="flex h-full w-80 flex-col overflow-y-auto overflow-x-hidden bg-neutral p-4 md:w-100">
            <div className="flex w-full flex-row items-center justify-between">
              <div className="font-bold text-base-content text-lg md:text-xl">
                {t("chatSession.overviewTitle")}
              </div>
              <div className="flex flex-row items-center gap-2">
                <IconWithTooltip
                  icon={<PlusIcon className="h-5 w-5" />}
                  tooltip={t("startNewChat")}
                  handler={() => {
                    setSearchParams({}, { replace: true });
                    setDrawerOpen(false);
                  }}
                  tooltipPosition="bottom"
                />
                <label
                  htmlFor={drawerId}
                  className="btn btn-ghost btn-sm btn-circle"
                  aria-label={t("chatSession.closeOverlay")}
                >
                  <XMarkIcon className="h-5 w-5" />
                </label>
              </div>
            </div>
            {sessionGroups.map(
              ({ sessions, title }) =>
                sessions.length > 0 && (
                  <div key={title}>
                    <div className="mt-4 mb-2 font-semibold text-lg">
                      {title}
                    </div>
                    {sessions.map((session) => (
                      <ChatSessionCard
                        key={session.id}
                        session={session}
                        isCurrent={session.id === currentChatSession?.id}
                        patchChatSession={patchChatSession}
                      />
                    ))}
                  </div>
                ),
            )}
          </div>
        )}
      </div>
      <div className="drawer-content flex h-full flex-col">
        <div className="flex flex-row items-center gap-2">
          <label
            htmlFor={drawerId}
            className="btn btn-ghost btn-circle h-fit w-fit p-1"
            aria-label={t("chatSession.openOverlay")}
          >
            <Bars3Icon className="h-6 w-6" />
          </label>
          <div className="font-bold text-lg">
            {currentChatSession?.name || t("chatSession.defaultNameSession")}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
